import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, X, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { authAPI, adminAPI } from '../services/api';
import { getVersionString } from '../version';
import ThemeToggle from '../components/ThemeToggle';

interface DetectionRule {
  name: string;
  enabled: boolean;
  platform: string;
  event_type: string;
  confidence: string;
  description: string;
  match: {
    host_contains?: string[];
    host_is?: string[];
    path_contains?: string[];
    path_starts?: string[];
    query_has?: string[];
    [key: string]: any;
  };
  extract?: {
    [key: string]: {
      from: string;
      param?: string;
      regex?: string;
    };
  };
}

interface RulesData {
  totalRules: number;
  rulesByPlatform: Record<string, DetectionRule[]>;
  allRules: DetectionRule[];
  filePath: string;
  lastModified: string;
}

export default function PatternSettings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rulesData, setRulesData] = useState<RulesData | null>(null);
  const [selectedPlatform, setSelectedPlatform] = useState<string>('all');

  useEffect(() => {
    loadRules();
  }, []);

  async function loadRules() {
    try {
      const authCheck = await authAPI.checkAdminAuth();
      if (!authCheck.authenticated) {
        navigate('/admin');
        return;
      }

      const data = await adminAPI.getDetectionRules();
      setRulesData(data);
    } catch (error) {
      console.error('Failed to load detection rules:', error);
    } finally {
      setLoading(false);
    }
  }

  function getPlatformColor(platform: string): string {
    const colors: Record<string, string> = {
      youtube: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
      facebook: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
      instagram: 'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300',
      tiktok: 'bg-black text-white',
      x_twitter: 'bg-gray-800 text-gray-100',
      generic: 'bg-gray-100 text-gray-700 dark:bg-gray-700/50 dark:text-gray-300',
    };
    return colors[platform] || 'bg-gray-100 text-gray-700 dark:bg-gray-700/50 dark:text-gray-300';
  }

  const platforms = rulesData ? Object.keys(rulesData.rulesByPlatform).sort() : [];
  const displayRules = selectedPlatform === 'all'
    ? rulesData?.allRules || []
    : rulesData?.rulesByPlatform[selectedPlatform] || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-kido-50 dark:bg-[#1a1612] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-kido-50 dark:bg-[#1a1612]">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white dark:bg-warm-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">{t('patternSettings.title')}</h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('patternSettings.detectionRules')}</p>
                <p className="text-xs text-gray-500 dark:text-gray-500">{getVersionString()}</p>
              </div>
            </div>
            <button
              onClick={loadRules}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              <RefreshCw className="w-4 h-4" />
              {t('common.refresh')}
            </button>
            <ThemeToggle />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white dark:bg-warm-gray-800 rounded-xl border border-warm-gray-200 dark:border-warm-gray-700 shadow-subtle p-3">
            <div className="text-sm text-gray-600 dark:text-gray-400">総ルール数</div>
            <div className="text-3xl font-bold text-gray-800 dark:text-gray-100">{rulesData?.totalRules || 0}</div>
          </div>
          <div className="bg-white dark:bg-warm-gray-800 rounded-xl border border-warm-gray-200 dark:border-warm-gray-700 shadow-subtle p-3">
            <div className="text-sm text-gray-600 dark:text-gray-400">{t('patternSettings.platform')}</div>
            <div className="text-3xl font-bold text-gray-800 dark:text-gray-100">{platforms.length}</div>
          </div>
          <div className="bg-white dark:bg-warm-gray-800 rounded-xl border border-warm-gray-200 dark:border-warm-gray-700 shadow-subtle p-3">
            <div className="text-sm text-gray-600 dark:text-gray-400">{t('patternSettings.enabled')}</div>
            <div className="text-3xl font-bold text-gray-800 dark:text-gray-100">
              {rulesData?.allRules.filter(r => r.enabled).length || 0}
            </div>
          </div>
        </div>

        {/* Platform Filter */}
        <div className="bg-white dark:bg-warm-gray-800 rounded-lg shadow p-4 mb-6">
          <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">プラットフォームで絞り込み</div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedPlatform('all')}
              className={`px-4 py-2 rounded ${
                selectedPlatform === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-warm-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-warm-gray-600'
              }`}
            >
              すべて ({rulesData?.totalRules || 0})
            </button>
            {platforms.map(platform => (
              <button
                key={platform}
                onClick={() => setSelectedPlatform(platform)}
                className={`px-4 py-2 rounded ${
                  selectedPlatform === platform
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-warm-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-warm-gray-600'
                }`}
              >
                {platform} ({rulesData?.rulesByPlatform[platform].length})
              </button>
            ))}
          </div>
        </div>

        {/* Rules List */}
        <div className="space-y-4">
          {displayRules.map((rule, index) => (
            <div key={index} className="bg-white dark:bg-warm-gray-800 rounded-lg shadow overflow-hidden">
              <div className="p-6">
                {/* Rule Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">{rule.name}</h3>
                      {rule.enabled ? (
                        <span className="flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-xs font-semibold">
                          <Check className="w-3 h-3" />
                          {t('patternSettings.enabled')}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-warm-gray-700 text-gray-600 dark:text-gray-400 rounded text-xs font-semibold">
                          <X className="w-3 h-3" />
                          {t('patternSettings.disabled')}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{rule.description}</p>
                  </div>
                </div>

                {/* Rule Details */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('patternSettings.platform')}</div>
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${getPlatformColor(rule.platform)}`}>
                      {rule.platform}
                    </span>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">イベント種別</div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">{rule.event_type}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">信頼度</div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">{rule.confidence}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">状態</div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {rule.enabled ? `✓ ${t('patternSettings.enabled')}` : `✗ ${t('patternSettings.disabled')}`}
                    </div>
                  </div>
                </div>

                {/* Match Conditions */}
                <div className="border-t border-gray-200 dark:border-warm-gray-700 pt-4">
                  <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">マッチ条件</div>
                  <div className="bg-gray-50 dark:bg-warm-gray-700 rounded p-3 space-y-2 text-sm">
                    {rule.match.host_contains && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Host contains:</span>
                        <span className="ml-2 text-gray-600 dark:text-gray-400">{rule.match.host_contains.join(', ')}</span>
                      </div>
                    )}
                    {rule.match.host_is && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Host is:</span>
                        <span className="ml-2 text-gray-600 dark:text-gray-400">{rule.match.host_is.join(', ')}</span>
                      </div>
                    )}
                    {rule.match.path_contains && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Path contains:</span>
                        <span className="ml-2 text-gray-600 dark:text-gray-400">{rule.match.path_contains.join(', ')}</span>
                      </div>
                    )}
                    {rule.match.path_starts && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Path starts:</span>
                        <span className="ml-2 text-gray-600 dark:text-gray-400">{rule.match.path_starts.join(', ')}</span>
                      </div>
                    )}
                    {rule.match.query_has && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Query has:</span>
                        <span className="ml-2 text-gray-600 dark:text-gray-400">{rule.match.query_has.join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Extract Fields */}
                {rule.extract && Object.keys(rule.extract).length > 0 && (
                  <div className="border-t border-gray-200 dark:border-warm-gray-700 pt-4 mt-4">
                    <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">抽出フィールド</div>
                    <div className="bg-gray-50 dark:bg-warm-gray-700 rounded p-3 space-y-2 text-sm">
                      {Object.entries(rule.extract).map(([field, config]) => (
                        <div key={field}>
                          <span className="font-medium text-gray-700 dark:text-gray-300">{field}:</span>
                          <span className="ml-2 text-gray-600 dark:text-gray-400">
                            {config.from} {config.param ? `(${config.param})` : ''}
                            {config.regex ? ` regex: ${config.regex}` : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {displayRules.length === 0 && (
          <div className="bg-white dark:bg-warm-gray-800 rounded-lg shadow p-12 text-center">
            <p className="text-gray-500 dark:text-gray-400">{t('common.noData')}</p>
          </div>
        )}

        {/* File Info */}
        {rulesData && (
          <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-sm">
            <div className="font-semibold text-blue-800 dark:text-blue-300 mb-1">設定ファイル情報</div>
            <div className="text-blue-700 dark:text-blue-400">
              パス: <span className="font-mono">{rulesData.filePath}</span>
            </div>
            <div className="text-blue-700 dark:text-blue-400">
              最終更新: {new Date(rulesData.lastModified).toLocaleString('ja-JP')}
            </div>
            <div className="text-blue-600 dark:text-blue-500 text-xs mt-2">
              ※ 変更は自動的に60秒以内にシステムに反映されます（Hot-reload機能）
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
